import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import Tables from '../../components/organisms/Tables/Tables';
import { useMetalsLedger } from './useClientDetails';

const gold_sip_columns = [
  { id: 'debitType', label: 'Debit Type', minWidth: 120, align: 'left' as const },
  { id: 'date', label: 'Date', minWidth: 110, align: 'left' as const },
  { id: 'amount', label: 'Amount', minWidth: 100, align: 'right' as const },
  { id: 'quantity', label: 'Quantity', minWidth: 100, align: 'right' as const },
  { id: 'goldValue', label: 'Gold Value', minWidth: 110, align: 'right' as const },
  { id: 'gstValue', label: 'GST Value', minWidth: 100, align: 'right' as const },
  { id: 'perGramPrice', label: 'Per gram price', minWidth: 120, align: 'right' as const },
];

const silver_sip_columns = [
  { id: 'debitType', label: 'Debit Type', minWidth: 120, align: 'left' as const },
  { id: 'date', label: 'Date', minWidth: 110, align: 'left' as const },
  { id: 'amount', label: 'Amount', minWidth: 100, align: 'right' as const },
  { id: 'quantity', label: 'Quantity', minWidth: 100, align: 'right' as const },
  { id: 'silverValue', label: 'Silver Value', minWidth: 110, align: 'right' as const },
  { id: 'gstValue', label: 'GST Value', minWidth: 100, align: 'right' as const },
  { id: 'perGramPrice', label: 'Per gram price', minWidth: 120, align: 'right' as const },
];

const formatCurrency = (value: number) =>
  `₹ ${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatTransactionPurpose = (purpose: string) =>
  purpose
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

type MetalsLedgerTransaction = {
  transactionPurpose: string;
  status: string;
  amount: number;
  grams: number;
  perGramPrice: number;
  gstValue: number;
  value: number;
  transactionTimeStamp: string;
};

const rowsData = (
  result: MetalsLedgerTransaction[],
  isGold: boolean,
): Array<Record<string, string>> => {
  return result.map((item, index) => ({
    code: `${item.transactionTimeStamp}-${item.amount}-${index}`,
    debitType: formatTransactionPurpose(item.transactionPurpose),
    date: dayjs(item.transactionTimeStamp).format('DD MMM YYYY'),
    amount: formatCurrency(item.amount),
    quantity: `${Number(item.grams).toLocaleString('en-IN', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    })} g`,
    ...(isGold
      ? { goldValue: formatCurrency(item.value) }
      : { silverValue: formatCurrency(item.value) }),
    gstValue: formatCurrency(item.gstValue),
    perGramPrice: formatCurrency(
      item.perGramPrice > 0 ? item.perGramPrice : item.grams > 0 ? item.value / item.grams : 0,
    ),
  }));
};

interface DigitalMetalSipTransactionsTableProps {
  clientId?: string;
  tab: string;
  search?: string;
  transactionTypeFilter?: string;
}

export const DigitalMetalSipTransactionsTable = ({
  clientId,
  tab,
  search = '',
  transactionTypeFilter = '',
}: DigitalMetalSipTransactionsTableProps) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const { data, isLoading } = useMetalsLedger({
    clientId,
    tab,
    page,
    pageSize,
    sortBy: 'id',
    sortDir: 'DESC',
    searchPhrase: search,
    transactionTypeFilter,
  });

  const meta = data?.data?.meta ?? {};
  const totalElements = meta.totalElements ?? 0;

  const rows = useMemo(
    () => rowsData(data?.data?.data ?? [], tab === 'DIGITAL_GOLD_SIP'),
    [data?.data?.data, tab],
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [search, transactionTypeFilter]);

  return (
    <div>
      <Tables
        totalData={totalElements}
        columns={tab === 'DIGITAL_GOLD_SIP' ? gold_sip_columns : silver_sip_columns}
        rows={rows}
        pagination={true}
        rowsPerPage={pageSize}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleRowClick={() => {}}
        loading={isLoading}
      />
    </div>
  );
};

export default DigitalMetalSipTransactionsTable;
